import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

import type { WorkItem } from "../homebase/unifiedWorkItems";

function resolvePriorityWeight(priority: string | null): number {
  const normalized = priority?.trim().toLowerCase();
  if (!normalized) return 1;
  if (["critical", "urgent"].includes(normalized)) return 5;
  if (["high", "p1"].includes(normalized)) return 4;
  if (["medium", "normal", "p2"].includes(normalized)) return 3;
  if (["low", "minor", "p3"].includes(normalized)) return 2;
  return 1;
}

function parseDateOrInfinity(value: string | null): number {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return Number.POSITIVE_INFINITY;
  return parsed;
}

function parseDateOrZero(value: string | null): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
}

function compareActivityDesc(left: WorkItem, right: WorkItem): number {
  const leftActivity = parseDateOrZero(left.activityAt);
  const rightActivity = parseDateOrZero(right.activityAt);
  return rightActivity - leftActivity;
}

function comparePriorityDesc(left: WorkItem, right: WorkItem): number {
  return resolvePriorityWeight(right.priority) - resolvePriorityWeight(left.priority);
}

function compareIdAsc(left: WorkItem, right: WorkItem): number {
  return left.id.localeCompare(right.id);
}

export function compareBoardItemsByStatus(
  status: CmmdTaskStatus,
  left: WorkItem,
  right: WorkItem,
): number {
  if (status === "todo" || status === "blocked") {
    const byPriority = comparePriorityDesc(left, right);
    if (byPriority !== 0) return byPriority;

    const byDueDate = parseDateOrInfinity(left.dueDate) - parseDateOrInfinity(right.dueDate);
    if (byDueDate !== 0) return byDueDate;

    const byActivity = compareActivityDesc(left, right);
    if (byActivity !== 0) return byActivity;

    return compareIdAsc(left, right);
  }

  if (status === "in_progress" || status === "in_review") {
    const byActivity = compareActivityDesc(left, right);
    if (byActivity !== 0) return byActivity;

    const byPriority = comparePriorityDesc(left, right);
    if (byPriority !== 0) return byPriority;

    return compareIdAsc(left, right);
  }

  if (status === "done" || status === "cancelled") {
    const byActivity = compareActivityDesc(left, right);
    if (byActivity !== 0) return byActivity;

    return compareIdAsc(left, right);
  }

  return compareIdAsc(left, right);
}
